import { useEffect, useMemo, useRef, useState } from "react";
import { hostLabel, isHttpUrl, guessPlatform } from "@oshimap/utils";
import { reportStatusLabel } from "@oshimap/config";
import { ep, session } from "../../lib/api";
import { Button } from "../../components/ui";
import { play, haptic, sparkle } from "../../lib/motion";
import { useReducedMotion } from "../../lib/reduced-motion";
import "../visit/visit.css";
import { useDialog } from "../../lib/use-dialog";
import { loadDraft, saveDraft, clearDraft, isDraftFresh } from "../../lib/draft";

interface Props {
  targetPlaceId?: string;
  targetName: string;
  onClose: () => void;
}

/**
 * P2: 출처 없이는 제출할 수 없다. 외부 콘텐츠를 복제하지 않고 URL 만 보관한다.
 * P6: 제보는 승인 전 원본을 덮어쓰지 않는다 — 서버가 DRAFT → SUBMITTED 로만 만든다.
 */
export function SubmitSheet({ targetPlaceId, targetName, onClose }: Props) {
  const dialog = useDialog(onClose);

  /*
   * 16 §12: 제보 중 앱이 내려가도 쓰던 글을 잃지 않는다.
   * 현장에서 한 손으로 쓰다 전화가 오면 앱이 내려간다. 그때 글이 사라지면
   * 다시 쓰지 않고, 그렇게 정보 하나가 영영 안 올라온다.
   */
  /*
   * 초안 키에 **사용자를 넣는다.** 넣지 않으면 `submit:<장소>` 하나를 그
   * 기기의 모든 계정이 나눠 쓴다 — A 가 쓰다 만 글이 B 가 같은 장소를
   * 제보하려고 열었을 때 "쓰다 만 내용을 되살렸어요" 로 살아난다.
   * 한 기기를 나눠 쓰는 것은 이 제품에서 드문 일이 아니다.
   * 멱등키(`requestId`)까지 함께 되살아나므로 B 의 제보가 A 의 제보로
   * 합쳐질 수도 있다.
   */
  const draftKey = `submit:${session.user?.id ?? "guest"}:${targetPlaceId ?? "new"}`;
  const restored = useMemo(() => {
    const d = loadDraft<{ title: string; summary: string; sourceUrl: string; requestId: string; savedAt: number }>(draftKey);
    return d && isDraftFresh(d.savedAt) ? d : null;
  }, [draftKey]);

  const [title, setTitle] = useState(restored?.title ?? "");
  const [summary, setSummary] = useState(restored?.summary ?? "");
  const [sourceUrl, setSourceUrl] = useState(restored?.sourceUrl ?? "");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ id: string; status: string } | null>(null);
  /* 멱등키. 재시도해도 중복 제보가 만들어지지 않는다 (서버 필수 필드).
     초안을 이어서 내도 중복 제보가 생기지 않게 함께 되살린다. */
  const [requestId] = useState(() =>
    restored?.requestId ?? `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);
  const [dismissedDraft, setDismissedDraft] = useState(false);
  const reduced = useReducedMotion();

  /*
   * 제보는 **생성 → 제출** 두 번의 요청이다. 그 사이에 시트를 닫거나
   * 로그아웃하면, 여기서 멈추지 않는 한 두 번째 요청이 그대로 나간다 —
   * 없어진 화면을 위해, 때로는 **다음 사람의 토큰으로**.
   *
   * 막는 것은 여기까지다: 다음 요청·늦은 결과·화면 갱신.
   * 서버가 이미 만든 제보가 사라진다는 뜻은 아니다 (DRAFT 로 남는다).
   */
  const alive = useRef(false);
  const operation = useRef<AbortController | null>(null);
  const owner = useRef<string | null>(session.user?.id ?? null);
  useEffect(() => {
    alive.current = true;
    /* 세션이 알려 오는 것이 전부 계정 교체는 아니다 — 닉네임을 바꿔도 온다.
       바뀐 것이 **누구인가**일 때만 멈춘다. */
    const unsubscribe = session.subscribe((state) => {
      const id = state.status === "authenticated" ? state.user.id : null;
      if (id === owner.current) return;
      owner.current = id;
      operation.current?.abort(); operation.current = null;
    });
    return () => {
      alive.current = false;
      operation.current?.abort(); operation.current = null;
      unsubscribe();
    };
  }, []);

  /* 글자마다 저장한다. "저장 버튼" 을 눌러야 지켜지는 초안은 초안이 아니다. */
  useEffect(() => {
    if (!title && !summary && !sourceUrl) return;
    saveDraft(draftKey, { title, summary, sourceUrl, requestId, savedAt: Date.now() });
  }, [draftKey, title, summary, sourceUrl, requestId]);

  const urlOk = isHttpUrl(sourceUrl);
  const canSubmit = title.trim().length > 1 && urlOk;

  const submit = async () => {
    setTouched(true);
    if (!canSubmit || operation.current || !alive.current) return;
    const controller = new AbortController();
    operation.current = controller;
    setBusy(true); setError(null);
    try {
      const created = await ep.createReport({
        clientRequestId: requestId,
        reportType: targetPlaceId ? "PLACE_CHANGE" : "NEW_PLACE",
        title: title.trim(),
        summary: summary.trim() || undefined,
        ...(targetPlaceId ? { targetPlaceId } : {}),
        sourceLinks: [{ platform: guessPlatform(sourceUrl), url: sourceUrl.trim(), isOfficial: false }],
      }, { signal: controller.signal });
      controller.signal.throwIfAborted();
      const submitted = await ep.submitReport(created.id, { signal: controller.signal });
      controller.signal.throwIfAborted();
      clearDraft(draftKey);
      haptic("STAMP");
      setDone({ id: submitted.id, status: submitted.status });
    } catch (e) {
      if (controller.signal.aborted) return;
      setError(e instanceof Error ? e.message : "제출하지 못했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      if (operation.current === controller) { operation.current = null; setBusy(false); }
    }
  };

  if (done) {
    return (
      <div ref={dialog.ref} className="om-visit" role="dialog" aria-modal="true" aria-label="제보 완료">
        <div className="om-visit__done">
          <div className={`om-visit__stamp${reduced ? "" : " mo-bounce"}`} aria-hidden="true"
            ref={(el) => { if (el && !reduced) sparkle(el, 4); }}>✎</div>
          <h2 className="om-visit__title">제보를 접수했어요</h2>
          <p className="om-visit__body">
            검수 후 반영됩니다. 승인 전에는 기존 정보를 덮어쓰지 않아요.
          </p>
          <p className="om-visit__rm">현재 상태: <b>{reportStatusLabel(done.status).label}</b></p>
          <Button variant="primary" block onClick={onClose}>확인</Button>
        </div>
      </div>
    );
  }

  return (
    <div ref={dialog.ref} className="om-visit" role="dialog" aria-modal="true" aria-label="정보 수정 제보">
      <div className="om-visit__form">
        <h2 className="om-visit__title">{targetPlaceId ? `${targetName} · 정보 제보` : "새로운 장소 제보"}</h2>
        <p className="om-visit__note">
          {targetPlaceId
            ? "검수 후 반영됩니다. 승인 전에는 기존 정보가 바뀌지 않아요."
            : "검수 후 지도에 올라갑니다. 바로 공개되지는 않아요."}
        </p>

        {/* 말없이 채워 넣으면 내가 쓴 글인지 헷갈린다. 되살렸다고 알리고 지울 길을 준다. */}
        {restored && !dismissedDraft ? (
          <p className="om-note om-draftnote" role="status">
            쓰다 만 내용을 되살렸어요.
            <button type="button" onClick={() => {
              clearDraft(draftKey);
              setTitle(""); setSummary(""); setSourceUrl("");
              setDismissedDraft(true);
            }}>새로 쓰기</button>
          </p>
        ) : null}

        <label className="om-field">
          <span>
            {targetPlaceId ? "무엇이 다른가요?" : "어떤 곳인가요?"} <b style={{ color: "var(--color-danger)" }}>*</b>
          </span>
          <input value={title} onChange={(e) => setTitle(e.target.value)}
            placeholder={targetPlaceId ? "예: 영업시간이 바뀌었어요" : "예: 최애의 아이 팝업 · 성수"}
            required maxLength={160} />
        </label>

        <label className="om-field">
          <span>자세히</span>
          <textarea value={summary} onChange={(e) => setSummary(e.target.value)} rows={3}
            placeholder={targetPlaceId ? "확인한 내용을 적어주세요" : "주소, 기간, 무엇을 파는지 적어주세요"}
            maxLength={2000} />
        </label>

        <label className="om-field">
          <span>출처 URL <b style={{ color: "var(--color-danger)" }}>*</b></span>
          <input value={sourceUrl} onChange={(e) => { setSourceUrl(e.target.value); setError(null); }}
            onBlur={() => setTouched(true)}
            placeholder="공식 계정 · 공지 · 기사 링크"
            aria-invalid={touched && !urlOk}
            style={touched && !urlOk ? { borderColor: "var(--color-danger)" } : undefined} />
          {touched && !urlOk ? (
            <span className="om-note om-note--err" role="alert" style={{ display: "block", marginTop: 6 }}>
              출처가 없으면 제출할 수 없습니다. OshiMap 은 원문을 복제하지 않고 링크만 보관합니다.
            </span>
          ) : urlOk ? (
            <span className="om-visit__note" style={{ display: "block", marginTop: 6 }}>
              출처: <b>{hostLabel(sourceUrl)}</b>
            </span>
          ) : null}
        </label>

        {error ? <p className="om-note om-note--err" role="alert">{error}</p> : null}

        <div className="om-visit__actions">
          <Button variant="ghost" onClick={onClose} disabled={busy}>취소</Button>
          <Button variant="primary" style={{ flex: 1 }} disabled={busy}
            onClick={(e) => { play(e.currentTarget, "mo-pop"); void submit(); }}>
            {busy ? "제출하는 중…" : "제출하기"}
          </Button>
        </div>
      </div>
    </div>
  );
}
